/**
 * HOPE Compliance Widget
 * 
 * Real-time HOPE compliance dashboard widget for the main command center.
 * Features:
 * - Real-time compliance % across all patients
 * - Visual breakdown by status (Green/Yellow/Red)
 * - Drill-down to individual patient timelines
 * - One-click access to complete overdue HUV/SFV visits
 * - SFV queue with urgency indicators
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Shield, Users, Clock, AlertTriangle, CheckCircle,
  ChevronRight, RefreshCw, Calendar, Activity,
  Bell, Play, Eye, ClipboardCheck, TrendingUp,
  AlertOctagon, Stethoscope, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HOPEComplianceWidget = ({ 
  isDark = false, 
  onPatientSelect,
  onNavigateToHOPE,
  compact = false 
}) => {
  const [loading, setLoading] = useState(true);
  const [executiveData, setExecutiveData] = useState(null);
  const [rnData, setRnData] = useState(null);
  const [sfvAlerts, setSfvAlerts] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [patientCompliance, setPatientCompliance] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [execRes, rnRes, sfvRes] = await Promise.all([
        fetch(`${API_URL}/api/hope-clinical/dashboard/executive`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/hope-clinical/dashboard/rn-command-center`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/hope-clinical/sfv-alerts?status=active`, { headers: getAuthHeaders() })
      ]);

      if (execRes.ok) setExecutiveData(await execRes.json());
      if (rnRes.ok) setRnData(await rnRes.json());
      if (sfvRes.ok) {
        const data = await sfvRes.json();
        setSfvAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch HOPE data:', error);
    }
    setLoading(false);
  };

  const fetchPatientCompliance = async (patientId) => {
    try {
      const response = await fetch(`${API_URL}/api/hope-clinical/compliance/${patientId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPatientCompliance(data);
      }
    } catch (error) {
      console.error('Failed to fetch patient compliance:', error);
    }
  };

  const handlePatientClick = async (patient) => {
    setSelectedPatient(patient);
    await fetchPatientCompliance(patient.patient_id);
    setShowPatientDetails(true);
  };

  const handleStartVisit = (patient, visitType) => {
    if (onPatientSelect) {
      onPatientSelect({
        patient_id: patient.patient_id,
        patient_name: patient.patient_name,
        visit_type: visitType
      });
    }
    toast.success(`Starting ${visitType} visit for ${patient.patient_name || patient.patient_id}`);
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Calculate stats
  const stats = {
    compliance: executiveData?.summary?.hope_compliance_rate || 100,
    total: rnData?.summary?.green_compliant + rnData?.summary?.yellow_at_risk + rnData?.summary?.red_overdue || 0,
    green: rnData?.summary?.green_compliant || 0,
    yellow: rnData?.summary?.yellow_at_risk || 0,
    red: rnData?.summary?.red_overdue || 0,
    sfvPending: sfvAlerts?.length || 0,
    sfvOnTime: executiveData?.sfv_metrics?.sfv_on_time_rate || 100
  };

  // Get urgent items (overdue + SFV pending)
  const urgentItems = [
    ...(rnData?.patients?.red || []).map(p => ({...p, urgency: 'overdue'})),
    ...sfvAlerts.filter(a => a.urgency === 'critical').map(a => ({
      patient_id: a.patient_id,
      patient_name: a.patient_name,
      urgency: 'sfv_critical',
      hours_until_due: a.hours_until_due,
      triggering_visit_id: a.triggering_visit_id
    }))
  ].slice(0, 5);

  if (loading) {
    return (
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"}>
        <CardContent className="p-4 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <span className={`ml-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>Loading HOPE data...</span>
        </CardContent>
      </Card>
    );
  }

  // Compact version for sidebar/widget
  if (compact) {
    return (
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-500" />
              HOPE Compliance
            </div>
            <Badge className={stats.compliance >= 90 ? "bg-green-500" : stats.compliance >= 70 ? "bg-amber-500" : "bg-red-500"}>
              {stats.compliance.toFixed(0)}%
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Progress value={stats.compliance} className="h-2 mb-3" />
          
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="p-2 rounded bg-green-50 dark:bg-green-900/20">
              <p className="text-lg font-bold text-green-600">{stats.green}</p>
              <p className="text-xs text-green-700 dark:text-green-400">OK</p>
            </div>
            <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20">
              <p className="text-lg font-bold text-amber-600">{stats.yellow}</p>
              <p className="text-xs text-amber-700 dark:text-amber-400">Due</p>
            </div>
            <div className="p-2 rounded bg-red-50 dark:bg-red-900/20">
              <p className="text-lg font-bold text-red-600">{stats.red}</p>
              <p className="text-xs text-red-700 dark:text-red-400">Overdue</p>
            </div>
          </div>

          {stats.sfvPending > 0 && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    {stats.sfvPending} SFV Pending
                  </span>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-600 h-7 px-2"
                  onClick={() => onNavigateToHOPE && onNavigateToHOPE('sfv-queue')}
                >
                  View
                </Button>
              </div>
            </div>
          )}

          <Button 
            className="w-full"
            size="sm"
            variant="outline"
            onClick={() => onNavigateToHOPE && onNavigateToHOPE()}
          >
            <Activity className="w-4 h-4 mr-2" />
            Open HOPE Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Full widget version
  return (
    <>
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-500" />
                HOPE Compliance Overview
              </CardTitle>
              <CardDescription>
                Real-time CMS HOPE compliance tracking
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Compliance Meter */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className={`font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                Overall Compliance
              </span>
              <span className={`text-2xl font-bold ${
                stats.compliance >= 90 ? 'text-green-600' : 
                stats.compliance >= 70 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {stats.compliance.toFixed(1)}%
              </span>
            </div>
            <Progress 
              value={stats.compliance} 
              className="h-3"
            />
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>Target: 95%</span>
              <span>SFV On-Time: {stats.sfvOnTime.toFixed(0)}%</span>
            </div>
          </div>

          {/* Patient Status Grid */}
          <div className="grid grid-cols-4 gap-3">
            <div 
              className={`p-3 rounded-lg text-center cursor-pointer transition-colors ${
                isDark ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-100 hover:bg-slate-200"
              }`}
              onClick={() => onNavigateToHOPE && onNavigateToHOPE('rn-view')}
            >
              <Users className={`w-5 h-5 mx-auto mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
              <p className={`text-xl font-bold ${isDark ? "text-slate-200" : "text-slate-700"}`}>{stats.total}</p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Total</p>
            </div>
            <div 
              className="p-3 rounded-lg text-center cursor-pointer transition-colors bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30"
              onClick={() => onNavigateToHOPE && onNavigateToHOPE('rn-view')}
            >
              <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <p className="text-xl font-bold text-green-600">{stats.green}</p>
              <p className="text-xs text-green-700 dark:text-green-400">Compliant</p>
            </div>
            <div 
              className="p-3 rounded-lg text-center cursor-pointer transition-colors bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30"
              onClick={() => onNavigateToHOPE && onNavigateToHOPE('rn-view')}
            >
              <Clock className="w-5 h-5 mx-auto mb-1 text-amber-500" />
              <p className="text-xl font-bold text-amber-600">{stats.yellow}</p>
              <p className="text-xs text-amber-700 dark:text-amber-400">Due Soon</p>
            </div>
            <div 
              className="p-3 rounded-lg text-center cursor-pointer transition-colors bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30"
              onClick={() => onNavigateToHOPE && onNavigateToHOPE('rn-view')}
            >
              <AlertOctagon className="w-5 h-5 mx-auto mb-1 text-red-500" />
              <p className="text-xl font-bold text-red-600">{stats.red}</p>
              <p className="text-xs text-red-700 dark:text-red-400">Overdue</p>
            </div>
          </div>

          {/* Urgent Actions */}
          {(urgentItems.length > 0 || stats.sfvPending > 0) && (
            <div className="space-y-2">
              <h4 className={`font-medium text-sm flex items-center gap-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Urgent Actions Required
              </h4>
              <ScrollArea className="h-[180px]">
                <div className="space-y-2">
                  {/* SFV Critical Alerts */}
                  {sfvAlerts.filter(a => a.urgency === 'critical').map((alert, idx) => (
                    <div 
                      key={`sfv-${idx}`}
                      className={`p-3 rounded-lg border ${
                        isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
                      }`}
                      data-testid={`urgent-sfv-${alert.patient_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-500 text-xs">SFV Critical</Badge>
                            <span className={`font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                              {alert.patient_name || alert.patient_id}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${isDark ? "text-red-400" : "text-red-600"}`}>
                            {alert.hours_until_due}h remaining • {alert.triggering_symptoms?.length || 0} symptoms
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => handleStartVisit({patient_id: alert.patient_id, patient_name: alert.patient_name}, 'SFV')}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Start SFV
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Overdue Patients */}
                  {(rnData?.patients?.red || []).slice(0, 3).map((patient, idx) => (
                    <div 
                      key={`overdue-${idx}`}
                      className={`p-3 rounded-lg border ${
                        isDark ? "bg-amber-900/20 border-amber-800" : "bg-amber-50 border-amber-200"
                      }`}
                      data-testid={`urgent-overdue-${patient.patient_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-500 text-xs">
                              {patient.sfv_pending ? 'SFV Needed' : 'HUV Overdue'}
                            </Badge>
                            <span className={`font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                              {patient.patient_name || patient.patient_id}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                            Day {patient.current_day} • {patient.next_action || 'Action required'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handlePatientClick(patient)}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => handleStartVisit(patient, patient.sfv_pending ? 'SFV' : 'HUV')}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Start
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* High Priority SFV Alerts */}
                  {sfvAlerts.filter(a => a.urgency === 'high').slice(0, 2).map((alert, idx) => (
                    <div 
                      key={`sfv-high-${idx}`}
                      className={`p-3 rounded-lg border ${
                        isDark ? "bg-slate-700 border-slate-600" : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">SFV Due</Badge>
                            <span className={`font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                              {alert.patient_name || alert.patient_id}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                            {alert.hours_until_due}h remaining
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleStartVisit({patient_id: alert.patient_id, patient_name: alert.patient_name}, 'SFV')}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          SFV
                        </Button>
                      </div>
                    </div>
                  ))}

                  {urgentItems.length === 0 && sfvAlerts.length === 0 && (
                    <div className={`text-center py-6 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                      <p className="font-medium">All caught up!</p>
                      <p className="text-sm">No urgent HOPE actions required</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onNavigateToHOPE && onNavigateToHOPE('sfv-queue')}
            >
              <Bell className="w-4 h-4 mr-2" />
              SFV Queue ({stats.sfvPending})
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => onNavigateToHOPE && onNavigateToHOPE()}
            >
              <Activity className="w-4 h-4 mr-2" />
              Full Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Patient Details Dialog */}
      <Dialog open={showPatientDetails} onOpenChange={setShowPatientDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-500" />
              {selectedPatient?.patient_name || selectedPatient?.patient_id}
            </DialogTitle>
            <DialogDescription>
              HOPE Compliance Timeline
            </DialogDescription>
          </DialogHeader>

          {patientCompliance && (
            <div className="space-y-4">
              {/* Compliance Status */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Overall Compliance</span>
                  <Badge className={
                    patientCompliance.overall_status === 'compliant' ? 'bg-green-500' :
                    patientCompliance.overall_status === 'at_risk' ? 'bg-amber-500' : 'bg-red-500'
                  }>
                    {patientCompliance.overall_status}
                  </Badge>
                </div>
                <Progress value={patientCompliance.compliance_percentage} className="h-2" />
                <p className="text-xs text-slate-500 mt-1">
                  Day {patientCompliance.current_day} since admission
                </p>
              </div>

              {/* Visit Schedule */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Visit Schedule</h4>
                
                {/* ADM */}
                <div className={`p-2 rounded border flex items-center justify-between ${
                  patientCompliance.adm_status === 'complete' ? 'bg-green-50 border-green-200' :
                  patientCompliance.adm_status === 'overdue' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {patientCompliance.adm_status === 'complete' ? 
                      <CheckCircle className="w-4 h-4 text-green-500" /> :
                      <Clock className="w-4 h-4 text-amber-500" />
                    }
                    <span className="text-sm font-medium">ADM (Admission)</span>
                    <span className="text-xs text-slate-500">Day 0</span>
                  </div>
                  <Badge variant="outline" className="text-xs capitalize">{patientCompliance.adm_status}</Badge>
                </div>

                {/* HUV1 */}
                <div className={`p-2 rounded border flex items-center justify-between ${
                  patientCompliance.huv1_status === 'complete' ? 'bg-green-50 border-green-200' :
                  patientCompliance.huv1_status === 'overdue' ? 'bg-red-50 border-red-200' :
                  patientCompliance.huv1_status === 'due' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {patientCompliance.huv1_status === 'complete' ? 
                      <CheckCircle className="w-4 h-4 text-green-500" /> :
                      patientCompliance.huv1_status === 'overdue' ?
                      <AlertOctagon className="w-4 h-4 text-red-500" /> :
                      <Clock className="w-4 h-4 text-amber-500" />
                    }
                    <span className="text-sm font-medium">HUV1 (Update 1)</span>
                    <span className="text-xs text-slate-500">Day 6-15</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">{patientCompliance.huv1_status}</Badge>
                    {patientCompliance.huv1_status === 'due' && (
                      <Button 
                        size="sm" 
                        className="h-6 px-2 bg-amber-500 hover:bg-amber-600"
                        onClick={() => {
                          handleStartVisit(selectedPatient, 'HUV1');
                          setShowPatientDetails(false);
                        }}
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* HUV2 */}
                <div className={`p-2 rounded border flex items-center justify-between ${
                  patientCompliance.huv2_status === 'complete' ? 'bg-green-50 border-green-200' :
                  patientCompliance.huv2_status === 'overdue' ? 'bg-red-50 border-red-200' :
                  patientCompliance.huv2_status === 'due' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {patientCompliance.huv2_status === 'complete' ? 
                      <CheckCircle className="w-4 h-4 text-green-500" /> :
                      patientCompliance.huv2_status === 'overdue' ?
                      <AlertOctagon className="w-4 h-4 text-red-500" /> :
                      <Clock className="w-4 h-4 text-slate-400" />
                    }
                    <span className="text-sm font-medium">HUV2 (Update 2)</span>
                    <span className="text-xs text-slate-500">Day 16-30</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">{patientCompliance.huv2_status}</Badge>
                    {patientCompliance.huv2_status === 'due' && (
                      <Button 
                        size="sm" 
                        className="h-6 px-2 bg-amber-500 hover:bg-amber-600"
                        onClick={() => {
                          handleStartVisit(selectedPatient, 'HUV2');
                          setShowPatientDetails(false);
                        }}
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* SFV Status */}
                {patientCompliance.sfv_pending && (
                  <div className="p-2 rounded border bg-red-50 border-red-300">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-700">SFV Required</span>
                      </div>
                      <Button 
                        size="sm" 
                        className="h-6 px-2 bg-red-600 hover:bg-red-700"
                        onClick={() => {
                          handleStartVisit(selectedPatient, 'SFV');
                          setShowPatientDetails(false);
                        }}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Start SFV
                      </Button>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      Due: {patientCompliance.sfv_due_date?.slice(0, 10) || 'ASAP'}
                    </p>
                  </div>
                )}
              </div>

              {/* Recent Visits */}
              {patientCompliance.visits?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Recent HOPE Visits</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {patientCompliance.visits.slice(0, 5).map((visit, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{visit.visit_type}</Badge>
                          <span className="text-slate-600 dark:text-slate-400">{visit.visit_date}</span>
                        </div>
                        {visit.sfv_triggered && (
                          <Badge className={visit.sfv_completed ? "bg-green-500" : "bg-red-500"} variant="default">
                            {visit.sfv_completed ? "SFV Done" : "SFV Pending"}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HOPEComplianceWidget;
